import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import NotificationBell from '../NotificationBell';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { LogOut, ChevronDown, Settings } from './navConfig';
import { Sparkles, Moon, Sun } from 'lucide-react';
import { restartOnboardingTour } from '../OnboardingTour';

const ROLE_BADGES = {
  admin: { label: 'Administrator', className: 'bg-primary/10 text-primary' },
  manager: { label: 'Manager', className: 'bg-accent/10 text-accent' },
  employee: { label: 'Mitarbeiter', className: 'bg-muted text-muted-foreground' },
};

export const UserMenu = ({ collapsed = false }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const roleBadge = ROLE_BADGES[user?.role] || ROLE_BADGES.employee;

  if (collapsed) {
    return (
      <div className="p-2 border-t border-border/40 flex flex-col items-center gap-2">
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-full hover:ring-2 hover:ring-primary/20 transition"
              data-testid="user-dropdown-trigger"
              aria-label="Benutzer-Menü"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${roleBadge.className}`}>{roleBadge.label}</span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
              <Settings className="mr-2 h-4 w-4" /> Einstellungen
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                restartOnboardingTour(user?.id);
                window.location.reload();
              }}
              data-testid="restart-onboarding"
            >
              <Sparkles className="mr-2 h-4 w-4" /> Tour erneut starten
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark'
                ? <><Sun className="mr-2 h-4 w-4" /> Heller Modus</>
                : <><Moon className="mr-2 h-4 w-4" /> Dunkler Modus</>}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-border/40">
      <div className="flex items-center justify-between mb-3 px-2">
        <span className="text-sm text-muted-foreground">Benachrichtigungen</span>
        <NotificationBell />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors"
            data-testid="user-dropdown-trigger"
          >
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadge.className}`}>{roleBadge.label}</span>
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
            <Settings className="mr-2 h-4 w-4" /> Einstellungen
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => e.preventDefault()}
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === 'dark'
              ? <><Sun className="mr-2 h-4 w-4" /> Heller Modus</>
              : <><Moon className="mr-2 h-4 w-4" /> Dunkler Modus</>}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" /> Abmelden
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
